import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Spinner } from '@/components/ui/spinner';

// Import the UserRole type from a shared types file if it exists
// Otherwise, define it here
import type { UserRole } from '@/types';

interface RouteRolesProps {
  children: React.ReactNode;
  /**
   * Array of roles that are allowed to access this route
   * If not provided, all authenticated users can access
   */
  allowedRoles?: UserRole[];
  /**
   * Where to redirect if user doesn't have required role
   * @default '/dashboard'
   */
  redirectTo?: string;
}

/**
 * Component that renders children only if user has one of the allowed roles.
 * Must be used inside a ProtectedRoute or another auth-protected component.
 */
export function RouteRoles({ 
  children, 
  allowedRoles, 
  redirectTo = '/dashboard' 
}: RouteRolesProps) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Spinner />;
  }

  // If no specific roles required, or user has one of the allowed roles
  if (!allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(user.rol as UserRole)) {
    return <>{children}</>;
  }

  // User doesn't have required role, redirect
  return <Navigate to={redirectTo} state={{ from: location }} replace />;
}
